import { Check } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@voll-intelligence/ui-kit/ui/toggle-group';
import type { HotelRoomView } from '../models.js';
import { ArrowStrip } from '../../shared/arrow_strip.js';
import type { HotelRoomsCopy } from './hotel_rooms.js';

/* Os chips de recorte de tarifa da "Escolha o quarto": a régua do recorte, o
   número de cada chip e a linha de estado do toque (#6470). */

export type HotelRoomFilter = 'policy' | 'breakfast' | 'refundable';

const filterMatches = (rate: HotelRoomView['rates'][number], filter: HotelRoomFilter) => {
  if (filter === 'policy') return rate.policyTone === 'within';
  if (filter === 'breakfast') return rate.breakfast === 'WITH_BREAKFAST';
  return rate.refund === 'WITH_REFUND';
};

const matchesFilters = (
  rate: HotelRoomView['rates'][number],
  filters: readonly HotelRoomFilter[],
) => filters.every((filter) => filterMatches(rate, filter));

/** Os chips filtram as TARIFAS; quarto que fica sem nenhuma sai da lista. A
 *  mesma régua na lista principal e em "Outras opções" (#5820): o recorte que a
 *  pessoa ligou não pode reaparecer no porão. As tarifas chegam na ordem da
 *  recomendação, então a primeira que sobra — a que o card mostra — é a melhor
 *  do recorte (#6470). */
export function filterRooms(
  rooms: readonly HotelRoomView[] | undefined,
  filters: readonly HotelRoomFilter[],
): readonly HotelRoomView[] {
  if (!rooms || filters.length === 0) return rooms ?? [];
  return rooms
    .map((room) => ({ ...room, rates: room.rates.filter((rate) => matchesFilters(rate, filters)) }))
    .filter((room) => room.rates.length > 0);
}

/** Quantos QUARTOS a lista mostra se ESTE chip for somado aos já ligados — o
 *  número que ele mostra. Contava tarifas, e "39" ao lado de uma lista de
 *  quartos não batia com nada à vista (#6470). Contar o chip sozinho prometia
 *  um recorte que a combinação não entregava ("Café da manhã 4" com a política
 *  ligada dando 0, #3845); a mesma régua incremental dos filtros da listagem.
 *  Chip já ligado mostra o resultado atual. */
function filterCount(
  rooms: readonly HotelRoomView[],
  filter: HotelRoomFilter,
  active: readonly HotelRoomFilter[],
): number {
  const filters = active.includes(filter) ? active : [...active, filter];
  return filterRooms(rooms, filters).length;
}

/** O que a lista mostra sob um recorte: cada quarto que fica, com a tarifa que
 *  o card exibe (a primeira que passa). */
function visibleList(rooms: readonly HotelRoomView[], filters: readonly HotelRoomFilter[]) {
  return filterRooms(rooms, filters)
    .map((room) => `${room.id}:${room.rates[0]?.id ?? ''}`)
    .join('|');
}

/** O chip não mexe em nada à vista: com ou sem ele (os outros como estão), os
 *  mesmos quartos com a mesma tarifa no card. Lista vazia não conta — ali quem
 *  fala é o "nenhum quarto". */
function filterChangesNothing(
  rooms: readonly HotelRoomView[],
  filters: readonly HotelRoomFilter[],
  filter: HotelRoomFilter,
): boolean {
  const others = filters.filter((active) => active !== filter);
  const without = visibleList(rooms, others);
  return without !== '' && without === visibleList(rooms, [...others, filter]);
}

/** O recorte dos chips e o chip que a pessoa tocou por último — é dele que a
 *  linha de estado fala. Abertura (#5820) e "Limpar filtros" não têm toque. */
export type HotelRoomFilterState = {
  filters: HotelRoomFilter[];
  toggled: HotelRoomFilter | undefined;
};

/** O retorno do toque que não mexeu em nada à vista (#6470): ligar ou soltar
 *  um chip que todo quarto já atende deixava a tela igual, e a pessoa não sabia
 *  se o toque tinha sido registrado. Sem linha quando a lista reagiu. */
function filterNotice(
  rooms: readonly HotelRoomView[],
  state: HotelRoomFilterState,
  options: readonly { id: HotelRoomFilter; label: string }[],
  copy: HotelRoomsCopy,
): string | undefined {
  const option = options.find(({ id }) => id === state.toggled);
  if (!option || !filterChangesNothing(rooms, state.filters, option.id)) return undefined;
  return copy.allRoomsMeetFilterLabel(option.label);
}

/** Os filtros são CORPORATIVOS (política, café, flexibilidade — o que a empresa
 *  cobra da reserva), como pílulas que rolam na horizontal com as setas da
 *  faixa: no drawer de 420px eles não cabem numa linha, e chip dobrado pra
 *  segunda linha empurra a lista inteira. O chip marca o ligado com o CHECK na
 *  frente e a contagem no selo redondo. */
export function HotelRoomFilterStrip({
  rooms,
  state,
  copy,
  onStateChange,
}: {
  readonly rooms: readonly HotelRoomView[];
  readonly state: HotelRoomFilterState;
  readonly copy: HotelRoomsCopy;
  readonly onStateChange: (state: HotelRoomFilterState) => void;
}) {
  const { filters } = state;
  const options: readonly { id: HotelRoomFilter; label: string }[] = [
    { id: 'policy', label: copy.withinPolicyFilterLabel },
    { id: 'breakfast', label: copy.breakfastFilterLabel },
    { id: 'refundable', label: copy.refundableFilterLabel },
  ];
  const notice = filterNotice(rooms, state, options, copy);
  return (
    /* `-mt-2` DESTE hospedeiro (os chips entram mais perto do título que o
       `gap-6` da lista) — a faixa não expõe `className`, regra do pacote. */
    <div data-slot="hotel-room-filters" className="-mt-2 min-w-0">
      <ArrowStrip
        scrollerSelector='[data-slot="toggle-group"]'
        previousLabel={copy.previousFilterLabel}
        nextLabel={copy.nextFilterLabel}
      >
        <ToggleGroup
          multiple
          value={filters}
          onValueChange={(value) => {
            const next = value as HotelRoomFilter[];
            onStateChange({
              filters: next,
              toggled:
                next.find((id) => !filters.includes(id)) ??
                filters.find((id) => !next.includes(id)),
            });
          }}
          variant="outline"
          size="sm"
          className="w-full min-w-0 flex-nowrap justify-start overflow-x-auto [scrollbar-width:none]"
        >
          {options.map(({ id, label }) => {
            const checked = filters.includes(id);
            const count = filterCount(rooms, id, filters);
            return (
              /* Zero-DESMARCADO tranca (a regra do `CheckFilterRow` da
                 listagem): ligá-lo só levaria ao "nenhum quarto". Zero-ligado
                 não tranca — a pessoa precisa conseguir desfazer o próprio
                 corte. */
              <ToggleGroupItem
                key={id}
                value={id}
                disabled={count === 0 && !checked}
                className="shrink-0 rounded-full"
              >
                {checked ? <Check aria-hidden /> : null}
                {label}
                <span className="bg-(--sand-75) text-muted-foreground rounded-full px-1.5 text-[11px] tabular-nums">
                  {count}
                </span>
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      </ArrowStrip>
      {/* Texto puro, sem região viva: é resposta ao toque da própria pessoa
          (a régua de anúncio do time só anuncia o que muda sem gesto). */}
      {notice ? <p className="text-caption text-muted-foreground mt-2">{notice}</p> : null}
    </div>
  );
}
